import type { ISecodaEditor } from '@repo/secoda-editor';
import insertChartBlock from '@repo/secoda-editor/commands/insertChartBlock';
import { getQueryBlocks } from '@repo/secoda-editor/lib/getQueryBlocks';
import { findParentNode } from 'prosemirror-utils';
import { useCallback, useMemo } from 'react';
import { useMetricList, useSearch } from '../../../../../api';
import { buildResourceUrl } from '../../../../../utils/navigationUtils';
import type {
	CreateGraphModalProps,
	CreateGraphModalResult,
} from './ChartBlock/CreateGraphModal';
import { CreateGraphModal } from './ChartBlock/CreateGraphModal';

export interface CreateGraphModalWrapperProps
	extends Pick<CreateGraphModalProps, 'onClose'> {
	editor: ISecodaEditor;
}

export function CreateGraphModalWrapper({
	onClose,
	editor,
}: CreateGraphModalWrapperProps) {
	const queryBlocks = getQueryBlocks(editor.view!.state.doc);
	const metricsIds = queryBlocks.map((qb) => qb.node.attrs.id);

	const { data } = useMetricList({
		filters: {
			hidden: false,
		},
	});

	const parentIds = (data?.results ?? [])
		.filter((m) => !!m.parent)
		.map((m) => m.parent) as string[];

	const { data: parentData } = useSearch({
		filterV2: {
			operator: 'in',
			operands: [],
			field: 'id',
			value: parentIds,
		},
		options: {
			enabled: !!parentIds.length,
		},
	});

	const metrics = useMemo(
		() =>
			(data?.results ?? []).map((metric) => {
				const parent = parentData?.results?.find((p) => p.id === metric.parent);
				return {
					...metric,
					inDoc: metricsIds.includes(metric.id),
					parentUrl: parent ? buildResourceUrl(parent) : undefined,
				};
			}),
		[data?.results, metricsIds, parentData?.results]
	);

	const onSave = useCallback(
		async (result: CreateGraphModalResult) => {
			if (!editor.view) {
				return;
			}

			const parent = findParentNode((node) => !!node)(
				editor.view.state.selection
			);

			if (parent) {
				insertChartBlock(editor.view, parent.pos, {
					id: result.metric.id,
					xAxis: result.xAxis,
					yAxis: result.yAxis,
					dimension: result.dimension,
					numericFormat: result.numericFormat,
				});
			}

			onClose();
		},
		[editor, onClose]
	);

	return (
		<CreateGraphModal metrics={metrics} onClose={onClose} onSave={onSave} />
	);
}
